import { useState, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CalendarDays, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FadeIn, Reveal } from "@/components/latir/Motion";
import { SectionLabel } from "@/components/latir/Ui";
import { events, performances, KIND_LABEL } from "@/data/agenda";
import { useCatalogVersion } from "@/data/catalogLive";
import { EditorialText } from "@/components/latir/EditorialText";
import { useSEO } from "@/hooks/useSEO";

/* ————————————————————————————————————————————————————————————————
 *  Calendar helpers — month grid + weekday labels
 * ———————————————————————————————————————————————————————————————— */
const WEEKDAYS = {
  es: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
};
const MONTH_LABELS = {
  septiembre: { es: "Septiembre", en: "September", num: 8, year: 2026 },
  octubre: { es: "Octubre", en: "October", num: 9, year: 2026 },
};

function monthGrid(year, monthIndex) {
  // returns 6×7 grid; each cell is null or a day number
  const first = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const startWeekday = first.getDay(); // 0..6
  const cells = Array.from({ length: 42 }, (_, i) => {
    const day = i - startWeekday + 1;
    return day > 0 && day <= daysInMonth ? day : null;
  });
  return cells;
}

/* ————————————————————————————————————————————————————————————————
 *  Add to Calendar helpers
 * ———————————————————————————————————————————————————————————————— */
// Given a Date object, format for Google / iCal as YYYYMMDDTHHMMSS (local)
function fmtICS(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    d.getFullYear() +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    "00"
  );
}

// Try to parse "6:00 p. m. – 9:00 p. m." into start/end Dates for a given day.
function parseTime(timeStr, year, monthIdx, day) {
  const dfl = { start: new Date(year, monthIdx, day, 18, 0), end: new Date(year, monthIdx, day, 20, 0) };
  if (!timeStr) return dfl;
  const norm = timeStr.toLowerCase().replace(/\s+/g, " ").replace(/\./g, "");
  const re = /(\d{1,2})(?::(\d{2}))?\s*(a m|p m|am|pm)?/g;
  const parts = [...norm.matchAll(re)];
  if (parts.length === 0) return dfl;
  const toDate = (m) => {
    let h = parseInt(m[1], 10);
    const min = m[2] ? parseInt(m[2], 10) : 0;
    const mer = (m[3] || "").replace(/\s/g, "");
    if (mer === "pm" && h < 12) h += 12;
    if (mer === "am" && h === 12) h = 0;
    return new Date(year, monthIdx, day, h, min);
  };
  const start = toDate(parts[0]);
  const end = parts[1] ? toDate(parts[1]) : new Date(start.getTime() + 60 * 60 * 1000);
  return { start, end };
}

function googleUrl({ title, description, location, start, end }) {
  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${fmtICS(start)}/${fmtICS(end)}`,
    details: description || "",
    location: location || "",
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

function outlookUrl({ title, description, location, start, end }) {
  const p = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: title,
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    body: description || "",
    location: location || "",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${p.toString()}`;
}

// General LATIR exhibition window (public opening) — Palacio Nacional
// 25 sep 2026 → 15 oct 2026, closed on Mondays.
function isGeneralExhibitionDay(monthKey, day) {
  if (!day) return false;
  if (monthKey === "septiembre" && day >= 25 && day <= 30) {
    return new Date(2026, 8, day).getDay() !== 1;
  }
  if (monthKey === "octubre" && day >= 1 && day <= 15) {
    return new Date(2026, 9, day).getDay() !== 1;
  }
  return false;
}

// Lunes dentro del rango LATIR (28 sep, 5 oct, 12 oct) → Palacio cerrado.
function isClosedMondayInLatir(monthKey, day) {
  if (!day) return false;
  if (monthKey === "septiembre" && day >= 25 && day <= 30) {
    return new Date(2026, 8, day).getDay() === 1;
  }
  if (monthKey === "octubre" && day >= 1 && day <= 15) {
    return new Date(2026, 9, day).getDay() === 1;
  }
  return false;
}

function icsDataUrl({ title, description, location, start, end }) {
  const uid = `${start.getTime()}@latir`;
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//LATIR//Programa 2026//ES",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${fmtICS(new Date())}`,
    `DTSTART:${fmtICS(start)}`,
    `DTEND:${fmtICS(end)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${(description || "").replace(/\n/g, "\\n")}`,
    `LOCATION:${location || ""}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

/* ————————————————————————————————————————————————————————————————
 *  Month calendar
 * ———————————————————————————————————————————————————————————————— */
function MonthCalendar({ monthKey, lang, onSelect, activeDay, activeMonth }) {
  const meta = MONTH_LABELS[monthKey];
  const grid = useMemo(() => monthGrid(meta.year, meta.num), [meta.year, meta.num]);
  const daysWithEvents = useMemo(
    () => new Set(events.filter((e) => e.month === monthKey).map((e) => e.day)),
    [monthKey]
  );

  // LATIR está abierta al público del 25 sep 2026 al 15 oct 2026.
  const isExhibitionDay = (day) => {
    if (!day) return false;
    if (monthKey === "septiembre" && day >= 25 && day <= 30) return true;
    if (monthKey === "octubre" && day >= 1 && day <= 15) return true;
    return false;
  };
  // Palacio Nacional cerrado los lunes durante LATIR.
  const isClosedMonday = (day) =>
    isExhibitionDay(day) && new Date(meta.year, meta.num, day).getDay() === 1;

  return (
    <div>
      <div className="flex items-baseline gap-5 mb-5">
        <h3
          className="font-bodoni font-normal text-[1.6rem] sm:text-[1.9rem] leading-none tracking-[-0.02em]"
          style={{ color: "#0A0A0A" }}
        >
          {meta[lang]}
          <span className="ml-3 label-ui text-ink-mute align-middle">{meta.year}</span>
        </h3>
        <span className="flex-1 h-px bg-line" />
      </div>

      {/* weekdays */}
      <div className="grid grid-cols-7 gap-y-2 label-ui text-ink-mute mb-2 text-[0.62rem]">
        {WEEKDAYS[lang].map((d) => (
          <div key={d} className="text-center">{d}</div>
        ))}
      </div>

      {/* days */}
      <div className="grid grid-cols-7 gap-y-2">
        {grid.map((day, i) => {
          if (!day) return <div key={i} />;
          const has = daysWithEvents.has(day);
          const isExhibit = isExhibitionDay(day);
          const isMondayClosed = isClosedMonday(day);
          // Cualquier día en rango es clicable (los lunes muestran "cerrado";
          // los demás muestran la tarjeta de "MUESTRA LATIR ABIERTA").
          const isClickable = has || isExhibit || isMondayClosed;
          const isActive = activeMonth === monthKey && activeDay === day;
          return (
            <button
              key={i}
              type="button"
              onClick={() => isClickable && onSelect(monthKey, day)}
              data-testid={`calendar-day-${monthKey}-${day}`}
              disabled={!isClickable}
              className={[
                "relative mx-auto flex flex-col items-center justify-center",
                "w-9 h-9 transition-all duration-300",
                isClickable ? "cursor-pointer" : "cursor-default",
              ].join(" ")}
              title={
                isMondayClosed
                  ? lang === "en"
                    ? "Palacio Nacional closed"
                    : "Palacio Nacional cerrado"
                  : undefined
              }
              aria-label={
                isMondayClosed
                  ? `${day} — ${lang === "en" ? "Palacio Nacional closed" : "Palacio Nacional cerrado"}`
                  : isExhibit
                  ? has
                    ? `${day} — ${lang === "en" ? "Exhibition open, with event" : "Exposición abierta, con evento"}`
                    : `${day} — ${lang === "en" ? "Exhibition open" : "Exposición abierta"}`
                  : has
                  ? `${day} — ${lang === "en" ? "Event" : "Evento"}`
                  : String(day)
              }
            >
              <span
                className={[
                  "inline-flex items-center justify-center rounded-full",
                  "font-editorial leading-none text-[0.95rem]",
                  isExhibit ? "w-[30px] h-[30px] font-medium" : "w-9 h-9",
                  isMondayClosed
                    ? "bg-transparent text-ink-mute/40 line-through hover:text-ink-mute/70"
                    : isExhibit
                    ? "bg-[rgba(20,20,20,0.05)] text-ink hover:bg-[rgba(20,20,20,0.10)]"
                    : isClickable
                    ? "text-ink hover:bg-paper-deep"
                    : "text-ink-mute/40",
                  isActive ? "!bg-paper-deep text-latir-red ring-1 ring-latir-red/70" : "",
                ].join(" ")}
              >
                {day}
              </span>
              {has && (
                <span className="absolute -bottom-0.5 w-1 h-1 rounded-full bg-latir-red" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ————————————————————————————————————————————————————————————————
 *  Add-to-calendar mini menu
 * ———————————————————————————————————————————————————————————————— */
function AddToCalendar({ evt, lang }) {
  const [open, setOpen] = useState(false);
  if (!evt) return null;
  const { start, end } = parseTime(
    evt.time,
    MONTH_LABELS[evt.month].year,
    MONTH_LABELS[evt.month].num,
    evt.day
  );
  const payload = {
    title: (lang === "en" && evt.titleEn) ? evt.titleEn : evt.title,
    description: (lang === "en" && evt.descriptionEn ? evt.descriptionEn : evt.description) || "",
    location: (lang === "en" && evt.locationEn ? evt.locationEn : evt.location) || "",
    start,
    end,
  };
  const opt = "block w-full text-left px-4 py-3 label-ui text-ink hover:text-latir-red hover:bg-paper-deep transition-colors";
  return (
    <div className="mt-4 md:mt-8">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="add-to-calendar-btn"
        className="inline-flex items-center gap-2 border border-ink/25 hover:border-latir-red text-ink hover:text-latir-red px-5 py-3 label-ui transition-colors"
      >
        <CalendarDays className="w-4 h-4" strokeWidth={1.4} />
        {lang === "en" ? "Add to my calendar" : "Agregar a mi calendario"}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.4} />
      </button>
      {open && (
        <div className="mt-3 border border-line divide-y divide-line bg-paper-raised">
          <a href={googleUrl(payload)} target="_blank" rel="noreferrer" className={opt}>Google Calendar</a>
          <a href={icsDataUrl(payload)} download={`${evt.title}.ics`} className={opt}>Apple Calendar (.ics)</a>
          <a href={outlookUrl(payload)} target="_blank" rel="noreferrer" className={opt}>Outlook</a>
        </div>
      )}
    </div>
  );
}

/* ————————————————————————————————————————————————————————————————
 *  Performance card — ficha horizontal compacta con "LEER MÁS"
 * ———————————————————————————————————————————————————————————————— */
function PerformanceCard({ p, lang }) {
  const [expanded, setExpanded] = useState(false);
  const title = lang === "en" && p.titleEn ? p.titleEn : p.title;
  const format = lang === "en" && p.formatEn ? p.formatEn : p.format;
  const inspiracion = lang === "en" && p.inspiracionEn ? p.inspiracionEn : p.inspiracion;
  const bio = lang === "en" && p.bioEn ? p.bioEn : p.bio;

  // Extracto: primera línea de la inspiración (aprox. 180 chars)
  const excerpt = (inspiracion || "").split(/\.\s+/).slice(0, 1).join(". ") + ".";
  const hasMore = Boolean(inspiracion || bio);

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-8 items-start">
      {/* Imagen — compacta, aspecto más apaisado en desktop */}
      <div className="md:col-span-4">
        <div className="relative bg-paper-deep overflow-hidden aspect-[4/5] md:aspect-[3/2] max-h-[300px] md:max-h-[220px]">
          <img
            src={p.image}
            alt={title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Info */}
      <div className="md:col-span-8">
        <span className="eyebrow text-latir-red">{format}</span>
        <h3 className="mt-2 font-display text-[1.4rem] sm:text-[1.6rem] leading-[1.05] tracking-[-0.02em] text-ink">
          {p.name}
        </h3>
        <p className="font-editorial italic text-[1.05rem] text-ink-soft mt-1">
          {title}
        </p>

        {/* Extracto o texto completo */}
        {!expanded && excerpt && (
          <p className="mt-3 font-editorial italic text-[0.98rem] leading-[1.5] text-ink-soft max-w-2xl line-clamp-3">
            {excerpt}
          </p>
        )}
        {expanded && (
          <div className="mt-3 space-y-3 max-w-2xl">
            {inspiracion && (
              <p className="font-editorial italic text-[0.98rem] leading-[1.55] text-ink-soft">
                {inspiracion}
              </p>
            )}
            {bio && (
              <p className="font-ui text-[0.85rem] leading-[1.6] text-ink-mute">
                {bio}
              </p>
            )}
          </div>
        )}

        {hasMore && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="performance-read-more"
            className="mt-3 inline-flex items-center gap-2 font-ui uppercase tracking-[0.2em] text-[0.66rem] text-latir-red hover:gap-3 transition-all"
          >
            {expanded
              ? (lang === "en" ? "Read less ←" : "Leer menos ←")
              : (lang === "en" ? "Read more →" : "Leer más →")}
          </button>
        )}
      </div>
    </div>
  );
}

/* ————————————————————————————————————————————————————————————————
 *  Page
 * ———————————————————————————————————————————————————————————————— */
export default function Agenda() {
  useCatalogVersion();
  useSEO({
    title: "Programa LATIR 2026 | Arteconciencia",
    description:
      "Consulta el programa de LATIR — El placer de vivir: exposiciones, conversatorios, visitas guiadas y actividades de Arteconciencia en el Palacio Nacional.",
    canonical: "/programa",
  });
  const { t, lang } = useLang();
  const [sel, setSel] = useState(null); // { month, day }

  const dayEvents = useMemo(() => {
    if (!sel) return [];
    return events.filter((e) => e.month === sel.month && e.day === sel.day);
  }, [sel]);

  const meta = sel ? MONTH_LABELS[sel.month] : null;

  return (
    <div className="pt-24 md:pt-28 pb-16 md:pb-20" data-testid="page-agenda">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        {/* header — compact */}
        <header className="mb-5 md:mb-6">
          <FadeIn>
            <span className="eyebrow text-ink-mute inline-flex items-center gap-3 mb-4">
              <span className="w-6 h-px bg-latir-red inline-block" />
              LATIR 2026
            </span>
          </FadeIn>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-end">
            <div className="lg:col-span-7">
              <h1
                className="font-bodoni font-normal text-[1.5rem] sm:text-[1.95rem] lg:text-[2.4rem] leading-[1.05] tracking-[-0.02em]"
                style={{ color: "#9D1116" }}
              >
                {t("agenda.title")}
              </h1>
            </div>
            <p
              className="lg:col-span-5 font-editorial text-[0.95rem] sm:text-[1rem] leading-[1.5] text-ink-soft max-w-[52ch]"
              data-testid="agenda-intro"
            >
              {t("agenda.intro")}
            </p>
          </div>
        </header>

        {/* Navegación rápida */}
        <nav
          className="mb-8 md:mb-10 border-t border-b border-line py-3 flex flex-wrap items-center gap-x-8 gap-y-2"
          aria-label={lang === "en" ? "Quick navigation" : "Navegación rápida"}
          data-testid="agenda-quick-nav"
        >
          {[
            { id: "calendario", es: "Calendario", en: "Calendar" },
            { id: "performance", es: "Performance", en: "Performance" },
            { id: "visita", es: "Información de visita", en: "Visit info" },
          ].map((it, i) => (
            <a
              key={it.id}
              href={`#${it.id}`}
              data-testid={`quicknav-${it.id}`}
              className={
                "font-ui uppercase tracking-[0.22em] text-[0.68rem] md:text-[0.72rem] text-ink hover:text-latir-red transition-colors " +
                (i > 0 ? "before:content-['·'] before:mr-8 before:text-ink-mute before:font-normal" : "")
              }
            >
              {it[lang]}
            </a>
          ))}
        </nav>

        {/* Performance (Casa Carmen) — ficha horizontal compacta */}
        {performances.length > 0 && (
          <section
            id="performance"
            className="mb-6 md:mb-8 scroll-mt-28"
            data-testid="agenda-performances-section"
          >
            {performances.map((p) => (
              <PerformanceCard key={p.slug} p={p} lang={lang} />
            ))}
          </section>
        )}

        {/* Aviso: muestra abierta durante todo el rango del Palacio */}
        <div
          id="calendario"
          className="max-w-[760px] mx-auto mb-4 font-editorial italic text-[0.95rem] sm:text-[1rem] leading-[1.5] text-ink-soft text-center scroll-mt-28"
          data-testid="agenda-open-notice"
        >
          {lang === "en"
            ? "Exhibition open from September 25 to October 15, 2026, during all Palacio Nacional opening days. Red dots mark special activities."
            : "Muestra abierta del 25 de septiembre al 15 de octubre de 2026, durante todos los días de apertura del Palacio Nacional. Los puntos rojos indican actividades especiales."}
        </div>

        {/* Legend — top */}
        <div className="max-w-[760px] mx-auto mb-6 flex flex-wrap items-center gap-6 label-ui text-ink-mute text-[0.62rem]">
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-3.5 h-3.5 rounded-full bg-[rgba(20,20,20,0.08)]" />
            {lang === "en" ? "Exhibition open" : "Exposición abierta"}
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-latir-red" />
            <span className="text-latir-red">{lang === "en" ? "Special event" : "Evento especial"}</span>
          </span>
        </div>

        {/* Two-column calendar */}
        <div className="grid grid-cols-1 gap-10 md:gap-12 mb-12 max-w-[760px] mx-auto">
          <Reveal>
            <MonthCalendar
              monthKey="septiembre"
              lang={lang}
              onSelect={(m, d) => setSel({ month: m, day: d })}
              activeDay={sel?.day}
              activeMonth={sel?.month}
            />
          </Reveal>
          <Reveal delay={0.1}>
            <MonthCalendar
              monthKey="octubre"
              lang={lang}
              onSelect={(m, d) => setSel({ month: m, day: d })}
              activeDay={sel?.day}
              activeMonth={sel?.month}
            />
          </Reveal>
        </div>

        {/* ============================================================
            HORARIO + TARIFAS — información pública del Palacio Nacional
            Dos columnas en desktop, una en móvil.
           ============================================================ */}
        <section
          id="visita"
          className="mt-10 md:mt-12 mb-4 max-w-[760px] mx-auto scroll-mt-28"
          data-testid="agenda-palacio-info"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 border-t border-line pt-8">
            {/* HORARIO */}
            <div data-testid="palacio-horario">
              <h3 className="eyebrow text-latir-red mb-4">
                {lang === "en" ? "Hours" : "Horario"}
              </h3>
              <p className="font-editorial text-[1.05rem] leading-[1.6] text-ink">
                {lang === "en" ? "Tuesday to Sunday" : "Martes a domingo"}
                <br />
                <span className="font-ui text-[0.95rem] tracking-[0.02em]">
                  10:00 a. m. – 12:00 a. m.
                </span>
              </p>
              <p className="mt-2 font-ui text-[0.85rem] text-ink-mute">
                {lang === "en" ? "Monday: Closed" : "Lunes cerrado"}
              </p>
            </div>

            {/* TARIFAS */}
            <div data-testid="palacio-tarifas">
              <h3 className="eyebrow text-latir-red mb-4">
                {lang === "en" ? "Admission to Palacio Nacional" : "Entrada al Palacio Nacional"}
              </h3>
              <ul className="font-ui text-[0.9rem] leading-[1.7] text-ink space-y-1.5">
                <li className="flex justify-between gap-6 border-b border-line/60 pb-1.5">
                  <span className="text-ink-soft">{lang === "en" ? "Salvadorans" : "Salvadoreños"}</span>
                  <span className="tabular-nums">US$1</span>
                </li>
                <li className="flex justify-between gap-6 border-b border-line/60 pb-1.5">
                  <span className="text-ink-soft">
                    {lang === "en"
                      ? "Central Americans and foreign residents"
                      : "Centroamericanos y extranjeros residentes"}
                  </span>
                  <span className="tabular-nums">US$3</span>
                </li>
                <li className="flex justify-between gap-6 border-b border-line/60 pb-1.5">
                  <span className="text-ink-soft">
                    {lang === "en" ? "Foreign non-residents" : "Extranjeros no residentes"}
                  </span>
                  <span className="tabular-nums">US$5</span>
                </li>
                <li className="flex justify-between gap-6 border-b border-line/60 pb-1.5">
                  <span className="text-ink-soft">
                    {lang === "en" ? "Students with valid ID" : "Estudiantes con carné vigente"}
                  </span>
                  <span className="font-ui uppercase tracking-[0.14em] text-[0.72rem] text-latir-red">
                    {lang === "en" ? "Free" : "Gratuita"}
                  </span>
                </li>
                <li className="flex justify-between gap-6 pb-1.5">
                  <span className="text-ink-soft">
                    {lang === "en" ? "Senior citizens" : "Personas de la tercera edad"}
                  </span>
                  <span className="font-ui uppercase tracking-[0.14em] text-[0.72rem] text-latir-red">
                    {lang === "en" ? "Free" : "Gratuita"}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Aclaración final */}
          <p
            className="mt-8 font-editorial italic text-[0.98rem] leading-[1.55] text-ink-soft text-center max-w-[560px] mx-auto"
            data-testid="palacio-info-note"
          >
            {lang === "en"
              ? "Access to LATIR is included in the Palacio Nacional admission."
              : "El acceso a la muestra LATIR está incluido en la entrada al Palacio Nacional."}
          </p>
        </section>

        {/* Visitas guiadas — reservation entry point */}
        <section className="mt-24" data-testid="agenda-visitas-section">
          <div className="flex items-baseline gap-6 mb-8">
            <SectionLabel>{lang === "en" ? "Guided visits" : "Visitas guiadas"}</SectionLabel>
            <span className="flex-1 h-px bg-line" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-7">
              <h2
                className="font-display text-[2rem] sm:text-[2.6rem] leading-[1.02] tracking-[-0.02em] text-latir-red mb-5"
                style={{ fontFamily: '"Instrument Serif", serif' }}
                data-testid="agenda-visitas-heading"
              >
                {lang === "en" ? "Book your visit" : "Agenda tu visita"}
              </h2>
              <p className="font-editorial italic text-[1.2rem] sm:text-[1.35rem] leading-[1.55] text-ink-soft max-w-2xl">
                {lang === "en"
                  ? "Explore LATIR with our curatorial team. Choose an available date and reserve your visit. Limited capacity."
                  : "Recorre LATIR acompañado por nuestro equipo curatorial. Selecciona una fecha disponible y reserva tu visita. Cupo limitado."}
              </p>
              <p className="mt-4 font-ui text-[0.85rem] leading-[1.6] text-ink-mute max-w-2xl">
                {lang === "en"
                  ? "Group visits are subject to confirmation. New dates may be enabled based on availability."
                  : "Las visitas para grupos estarán sujetas a confirmación. Nuevas fechas podrán habilitarse según disponibilidad."}
              </p>
            </div>
            <div className="md:col-span-5 md:pt-2">
              <a
                href="/reservas"
                data-testid="agenda-reservar-cta"
                className="inline-flex items-center gap-3 bg-ink text-paper-raised px-7 py-4 font-ui uppercase tracking-[0.22em] text-[0.72rem] hover:bg-latir-red transition-colors duration-500"
              >
                {lang === "en" ? "Reserve visit" : "Reservar visita"}
                <span className="w-4 h-px bg-current" />
              </a>
            </div>
          </div>
        </section>

        {/* Performances (Casa Carmen) moved above, right after the header intro. */}
      </div>

      {/* Slide-out event drawer */}
      <Sheet open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <SheetContent side="right" className="w-full sm:max-w-[520px] bg-paper border-l border-line p-0 overflow-y-auto">
          {meta && sel && (() => {
            const openDay = isGeneralExhibitionDay(sel.month, sel.day);
            const closedMon = isClosedMondayInLatir(sel.month, sel.day);
            const hasEvents = dayEvents.length > 0;

            return (
              <div className="p-5 md:p-12 pt-10 md:pt-14">
                {/* Fecha */}
                <SheetHeader className="text-left mb-6">
                  <p className="label-ui text-latir-red mb-1">
                    {sel.day} {meta[lang]} · {meta.year}
                  </p>
                  <SheetTitle className="sr-only">
                    {closedMon
                      ? (lang === "en" ? "Palacio Nacional closed" : "Palacio Nacional cerrado")
                      : openDay
                      ? "LATIR"
                      : hasEvents
                      ? (lang === "en" ? "Programme" : "Programa del día")
                      : ""}
                  </SheetTitle>
                </SheetHeader>

                {/* ============ Lunes en rango LATIR: PALACIO CERRADO ============ */}
                {closedMon && (
                  <section data-testid="closed-monday-card" className="mb-6">
                    <span className="inline-flex items-center gap-2 label-ui text-ink-mute">
                      <span className="w-1.5 h-1.5 rounded-full bg-ink-mute/60 inline-block" />
                      {lang === "en" ? "Closed" : "Cerrado"}
                    </span>
                    <h3 className="mt-3 font-bodoni font-normal text-[1.7rem] leading-[1.1] text-ink">
                      {lang === "en" ? "Palacio Nacional closed" : "Palacio Nacional cerrado"}
                    </h3>
                    <p className="mt-3 font-editorial italic text-[1.05rem] leading-[1.55] text-ink-soft">
                      {lang === "en"
                        ? "LATIR is not open on Mondays."
                        : "La muestra LATIR no está abierta los lunes."}
                    </p>
                  </section>
                )}

                {/* ============ 1) EVENTO(S) ESPECIAL(ES) DEL DÍA — jerarquía alta ============ */}
                {hasEvents && (
                  <section data-testid="event-drawer" className={openDay ? "mb-6 md:mb-10 pb-6 md:pb-10 border-b border-line" : ""}>
                    <p className="eyebrow text-latir-red mb-4 inline-flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-latir-red inline-block" />
                      {dayEvents.length > 1
                        ? (lang === "en" ? "Special activities" : "Actividades especiales")
                        : (lang === "en" ? "Special activity" : "Actividad especial")}
                    </p>
                    <div className="space-y-5 md:space-y-10">
                      {dayEvents.map((ev, i) => {
                        const kindLabel = KIND_LABEL[ev.kind]?.[lang] ?? "";
                        const title = lang === "en" && ev.titleEn ? ev.titleEn : ev.title;
                        const by = lang === "en" && ev.byEn ? ev.byEn : ev.by;
                        const location = lang === "en" && ev.locationEn ? ev.locationEn : ev.location;
                        const time = lang === "en" && ev.timeEn ? ev.timeEn : ev.time;
                        const description = lang === "en" && ev.descriptionEn ? ev.descriptionEn : ev.description;
                        return (
                          <article key={i} className={i > 0 ? "pt-6 md:pt-10 border-t border-line" : ""} data-testid={`event-card-${i}`}>
                            {/* MOBILE-FIRST: hora + título + tipo + Añadir al calendario, visibles siempre */}
                            {time && (
                              <p className="font-ui text-[0.92rem] tracking-[0.02em] text-ink mb-1 md:hidden" data-testid={`event-time-mobile-${i}`}>
                                {time}
                              </p>
                            )}
                            <h3 className="font-bodoni font-normal text-[1.35rem] md:text-[1.7rem] leading-[1.15] md:leading-[1.1] text-ink mb-2 md:mb-3">
                              {title}
                            </h3>
                            {kindLabel && (
                              <p className="mb-3 md:hidden inline-flex items-center gap-2 label-ui" data-testid={`event-kind-mobile-${i}`}>
                                <span className="w-3 h-px bg-latir-red inline-block" />
                                <span className="text-latir-red">{kindLabel}</span>
                              </p>
                            )}
                            {by && (
                              <p className="font-editorial italic text-[1rem] md:text-[1.05rem] text-ink-soft mb-3 md:mb-4 hidden md:block">
                                {by}
                              </p>
                            )}
                            {/* Desktop: dl completo con hora/lugar/costo. En mobile solo aparece dentro de "Ver detalles" */}
                            <dl className="space-y-2 label-ui hidden md:block">
                              {time && (
                                <div className="flex gap-3">
                                  <dt className="text-ink-mute w-16">{lang === "en" ? "Time" : "Hora"}</dt>
                                  <dd className="text-ink normal-case tracking-normal font-ui text-[0.95rem]">{time}</dd>
                                </div>
                              )}
                              {location && (
                                <div className="flex gap-3">
                                  <dt className="text-ink-mute w-16">{lang === "en" ? "Where" : "Lugar"}</dt>
                                  <dd className="text-ink normal-case tracking-normal font-ui text-[0.95rem]">{location}</dd>
                                </div>
                              )}
                              {ev.cost && (
                                <div className="flex gap-3">
                                  <dt className="text-ink-mute w-16">{lang === "en" ? "Cost" : "Costo"}</dt>
                                  <dd className="text-ink normal-case tracking-normal font-ui text-[0.95rem]">{ev.cost}</dd>
                                </div>
                              )}
                            </dl>
                            {description && (
                              <p className="mt-5 font-editorial text-[1.05rem] leading-relaxed text-ink-soft hidden md:block">
                                {description}
                              </p>
                            )}
                            {kindLabel && (
                              <p className="mt-5 hidden md:inline-flex items-center gap-2 label-ui">
                                <span className="w-4 h-px bg-latir-red inline-block" />
                                <span className="text-latir-red">{kindLabel}</span>
                              </p>
                            )}
                            {/* CTA Añadir al calendario — visible en mobile inmediatamente después del tipo */}
                            <AddToCalendar evt={ev} lang={lang} />

                            {/* Mobile only: "Ver detalles +" con by, location, cost, description */}
                            {(by || location || ev.cost || description) && (
                              <details className="md:hidden mt-3 group" data-testid={`event-details-mobile-${i}`}>
                                <summary className="inline-flex items-center gap-2 font-ui uppercase tracking-[0.2em] text-[0.66rem] text-latir-red cursor-pointer list-none select-none">
                                  <span className="group-open:hidden">{lang === "en" ? "View details" : "Ver detalles"} +</span>
                                  <span className="hidden group-open:inline">{lang === "en" ? "Hide details" : "Ocultar detalles"} −</span>
                                </summary>
                                <div className="mt-3 space-y-2.5">
                                  {by && (
                                    <p className="font-editorial italic text-[0.98rem] text-ink-soft">{by}</p>
                                  )}
                                  <dl className="space-y-1.5 label-ui">
                                    {location && (
                                      <div className="flex gap-3">
                                        <dt className="text-ink-mute w-14">{lang === "en" ? "Where" : "Lugar"}</dt>
                                        <dd className="text-ink normal-case tracking-normal font-ui text-[0.9rem]">{location}</dd>
                                      </div>
                                    )}
                                    {ev.cost && (
                                      <div className="flex gap-3">
                                        <dt className="text-ink-mute w-14">{lang === "en" ? "Cost" : "Costo"}</dt>
                                        <dd className="text-ink normal-case tracking-normal font-ui text-[0.9rem]">{ev.cost}</dd>
                                      </div>
                                    )}
                                  </dl>
                                  {description && (
                                    <p className="font-editorial text-[0.98rem] leading-relaxed text-ink-soft">
                                      {description}
                                    </p>
                                  )}
                                </div>
                              </details>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* ============ 2) INFORMACIÓN GENERAL DE LA MUESTRA + HORARIO PALACIO ============ */}
                {openDay && (
                  <section
                    data-testid="latir-open-card"
                    className={hasEvents ? "opacity-95" : ""}
                  >
                    <span className={hasEvents ? "label-ui text-ink-mute inline-flex items-center gap-2" : "eyebrow text-latir-red inline-flex items-center gap-2"}>
                      <span className={"w-1.5 h-1.5 rounded-full inline-block " + (hasEvents ? "bg-ink-mute/60" : "bg-latir-red")} />
                      {lang === "en" ? "LATIR exhibition open" : "Muestra LATIR abierta"}
                    </span>
                    <h3
                      className={
                        hasEvents
                          ? "mt-2 font-display text-[1.25rem] leading-[1.15] text-ink"
                          : "mt-3 font-bodoni font-normal text-[1.7rem] leading-[1.1] text-ink"
                      }
                    >
                      LATIR — {lang === "en" ? "The pleasure of living" : "El placer de vivir"}
                    </h3>
                    <p className={"font-editorial italic text-ink-soft " + (hasEvents ? "mt-1 text-[0.95rem]" : "mt-2 text-[1.02rem]")}>
                      Palacio Nacional de El Salvador
                    </p>
                    <p className={"font-ui text-ink " + (hasEvents ? "mt-2 text-[0.88rem]" : "mt-3 text-[0.92rem]")}>
                      10:00 a. m. – 12:00 a. m.
                    </p>

                    {/* Tarifas */}
                    <div className={hasEvents ? "mt-4 pt-4 border-t border-line/70" : "mt-6 pt-5 border-t border-line/70"}>
                      <h4 className="eyebrow text-latir-red mb-3">
                        {lang === "en" ? "Admission to Palacio Nacional" : "Entrada al Palacio Nacional"}
                      </h4>
                      <ul className={"font-ui leading-[1.7] text-ink space-y-1.5 " + (hasEvents ? "text-[0.85rem]" : "text-[0.9rem]")}>
                        <li className="flex justify-between gap-6 border-b border-line/60 pb-1.5">
                          <span className="text-ink-soft">{lang === "en" ? "Salvadorans" : "Salvadoreños"}</span>
                          <span className="tabular-nums">US$1</span>
                        </li>
                        <li className="flex justify-between gap-6 border-b border-line/60 pb-1.5">
                          <span className="text-ink-soft">
                            {lang === "en"
                              ? "Central Americans and foreign residents"
                              : "Centroamericanos y extranjeros residentes"}
                          </span>
                          <span className="tabular-nums">US$3</span>
                        </li>
                        <li className="flex justify-between gap-6 border-b border-line/60 pb-1.5">
                          <span className="text-ink-soft">{lang === "en" ? "Foreign non-residents" : "Extranjeros no residentes"}</span>
                          <span className="tabular-nums">US$5</span>
                        </li>
                        <li className="flex justify-between gap-6 border-b border-line/60 pb-1.5">
                          <span className="text-ink-soft">
                            {lang === "en" ? "Students with valid ID" : "Estudiantes con carné vigente"}
                          </span>
                          <span className="font-ui uppercase tracking-[0.14em] text-[0.68rem] text-latir-red">
                            {lang === "en" ? "Free" : "Gratuita"}
                          </span>
                        </li>
                        <li className="flex justify-between gap-6 pb-1.5">
                          <span className="text-ink-soft">
                            {lang === "en" ? "Senior citizens" : "Personas de la tercera edad"}
                          </span>
                          <span className="font-ui uppercase tracking-[0.14em] text-[0.68rem] text-latir-red">
                            {lang === "en" ? "Free" : "Gratuita"}
                          </span>
                        </li>
                      </ul>
                      <p className={"font-editorial italic text-ink-soft leading-[1.55] " + (hasEvents ? "mt-3 text-[0.85rem]" : "mt-4 text-[0.9rem]")}>
                        {lang === "en"
                          ? "Access to LATIR is included in the Palacio Nacional admission."
                          : "El acceso a la muestra LATIR está incluido en la entrada al Palacio Nacional."}
                      </p>
                    </div>

                    {!hasEvents && (
                      <AddToCalendar
                        lang={lang}
                        evt={{
                          title: `LATIR — ${lang === "en" ? "General visit" : "Visita general"}`,
                          titleEn: "LATIR — General visit",
                          description:
                            lang === "en"
                              ? "General admission to LATIR: The pleasure of living at Palacio Nacional de El Salvador. Hours 10:00 a.m. – midnight."
                              : "Entrada general a LATIR: El placer de vivir en el Palacio Nacional de El Salvador. Horario 10:00 a. m. – 12:00 a. m.",
                          location: "Palacio Nacional de El Salvador",
                          month: sel.month,
                          day: sel.day,
                          time: "10:00 a. m. – 12:00 a. m.",
                        }}
                      />
                    )}
                  </section>
                )}
              </div>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}
